import { fetchAccumulatedOfferCommissionById } from 'actions/AgentHubActions'
import { useContext, useEffect, useRef } from 'react'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { max } from 'lib/array/arrayUtils'
import { getAgentHubOfferCommissionBody, getAgentHubOfferCommissionBodyForBundledFlow, getOfferSummary } from 'agentHub/selectors/agentHubSelectors'
import { fetchAccumulatedOfferById } from 'actions/OfferActions'
import config from 'constants/config'
import { mapOfferToAgentHubCommissionPayload } from 'agentHub/api/mappers/commissionMap'
import { getCheckInDateFromURLSearchParams, getCheckOutDateFromURLSearchParams, getOccupanciesFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useQueryParams from 'hooks/useQueryParams'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { mapBedbankPackageToAgentHubPayload } from 'agentHub/api/mappers/agentHubMap'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'

interface UseAgentHubCommissionDateOverrides {
  checkIn?: string
  checkOut?: string
  rooms?: Array<App.Occupants>
}

function useAgentHubCommission(
  offerId: string,
  /**
   * @param offer - optional, to be used when the offer is not found by redux store (e.g. car hire and experience offers)
   */
  offer?: App.AgentHubOfferPayload,
  /**
   * @param dateOverrides - optional, when provided these values are used instead of URL query params
   * for bedbank rate key resolution and commission fetch parameters (e.g. tour accommodation step where
   * dates come from component state rather than URL).
   */
  dateOverrides?: UseAgentHubCommissionDateOverrides,
) {
  const dispatch = useAppDispatch()
  const offerPageState = useContext(OfferPageStateContext)
  const hasRequestedCommissionRef = useRef(false)
  const lastRequestedDateKeyRef = useRef<string | undefined>(undefined)

  const offerSummary = useAppSelector(state => getOfferSummary(state, offerId))

  const agentHubOfferCommissionBody = useAppSelector(state => getAgentHubOfferCommissionBody(state, offerId))

  const mappedOfferParameter = offer && 'type' in offer && offer.type !== 'bedbank_hotel' ? mapOfferToAgentHubCommissionPayload(offer) : null

  const flightWithJourneyCommissionBody = useAppSelector(state => offer && 'journey' in offer ? getAgentHubOfferCommissionBodyForBundledFlow(state, offer) : null)

  const mappedAgentHubOfferCommission = agentHubOfferCommissionBody ?? mappedOfferParameter ?? flightWithJourneyCommissionBody

  const isFetchRequired = config.agentHub.isEnabled && (offerSummary || offer) && !mappedAgentHubOfferCommission

  const offerCommissionData = useAppSelector(state => state.agentHub.offersCommissionMap?.[offerId])
  const cachedDateKey = useAppSelector(state => state.agentHub.offersCommissionDateKeys?.[offerId])

  const commissionPercentage = offerCommissionData?.commissionPercentage ?? 0
  const packagesCommission = offerCommissionData?.packages ?? []
  const minBaseCommission = offerCommissionData?.minBaseCommission || commissionPercentage
  const maxBaseCommission = offerCommissionData?.maxBaseCommission || commissionPercentage
  const greatestPackageCommission = max(packagesCommission, pkg => pkg.commissionPercentage)?.commissionPercentage ?? 0
  const dateRangesCommission = offerCommissionData?.dateRangesCommission ?? {}
  const includesTaxesAndFees = offerCommissionData?.includesTaxesAndFees ?? true
  const bedbankOffer = useAppSelector(state => state.offer.bedbankOfferRates?.[offerId]) || {}

  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchAccumulatedOfferById(offerId))
    }
  }, [offerId, isFetchRequired, dispatch])

  const queryParams = useQueryParams()
  const hasDateOverrides = !!(dateOverrides?.checkIn && dateOverrides?.checkOut && dateOverrides?.rooms)
  const isDatePickerOpen = offerPageState.activeFilter === 'checkin' || offerPageState.activeHeaderFilter === 'checkin'

  useEffect(() => {
    if (config.agentHub.isEnabled && mappedAgentHubOfferCommission) {
      let startDate: string | undefined
      let endDate: string | undefined
      let occupancies: Array<App.Occupants> | undefined

      if (hasDateOverrides) {
        startDate = dateOverrides.checkIn
        endDate = dateOverrides.checkOut
        occupancies = dateOverrides.rooms
      } else {
        const isFlightCommission = mappedAgentHubOfferCommission.type === 'flight'
        const flightDepartDate = queryParams.get('departDate') ?? undefined
        const flightReturnDate = queryParams.get('returnDate') ?? undefined
        const checkInParam = getCheckInDateFromURLSearchParams(queryParams)
        const checkOutParam = getCheckOutDateFromURLSearchParams(queryParams)
        occupancies = getOccupanciesFromURLSearchParams(queryParams)
        startDate = checkInParam?.format(ISO_DATE_FORMAT)
        endDate = checkOutParam?.format(ISO_DATE_FORMAT)

        if (isFlightCommission && flightDepartDate) {
          startDate = flightDepartDate
          endDate = flightReturnDate ?? flightDepartDate
        }
      }

      const dateKey = startDate && endDate ? `${startDate}-${endDate}` : undefined
      const latestRequestedDateKey = lastRequestedDateKeyRef.current ?? cachedDateKey
      const hasRequestedCommission = hasRequestedCommissionRef.current || !!offerCommissionData || cachedDateKey !== undefined
      // Defer commission refetch while the date picker is open to avoid
      // unnecessary API requests as the user selects dates. Allow the initial
      // fetch (before any commission data exists) to proceed regardless.
      if (isDatePickerOpen && hasRequestedCommission && latestRequestedDateKey !== dateKey) {
        return
      }

      const bodyToSend =
        agentHubOfferCommissionBody?.type === 'bedbank_hotel' && bedbankOffer && occupancies && startDate && endDate
          ? {
              ...agentHubOfferCommissionBody,
              packages: (bedbankOffer[getBedbankRateKey(occupancies, startDate, endDate)] || []).map(mapBedbankPackageToAgentHubPayload),
            }
          : mappedAgentHubOfferCommission.type === 'flight' && startDate && endDate
            ? {
                ...mappedAgentHubOfferCommission,
                startDate,
                endDate,
              }
            : mappedAgentHubOfferCommission

      hasRequestedCommissionRef.current = true
      lastRequestedDateKeyRef.current = dateKey
      dispatch(fetchAccumulatedOfferCommissionById(offerId, bodyToSend, {
        startDate,
        endDate,
        occupants: occupancies,
      }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [agentHubOfferCommissionBody, hasDateOverrides, dateOverrides?.checkIn, dateOverrides?.checkOut, dateOverrides?.rooms, queryParams.get('checkIn'), queryParams.get('checkOut'), queryParams.get('occupancies'), queryParams.get('departDate'), queryParams.get('returnDate'), isDatePickerOpen])

  return { commissionPercentage, packagesCommission, greatestPackageCommission, minBaseCommission, maxBaseCommission, dateRangesCommission, includesTaxesAndFees }
}

export default useAgentHubCommission
